import * as React from 'react';
import { useState } from 'react';
import { useNotify, useRefresh, useGetIdentity } from 'react-admin';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  sendWorkItem,
  dismissWorkItem,
  snoozeWorkItem,
  confirmWorkItem,
  resendPaperworkLink,
} from './workItemsApi';
import { PartnerDocsWorkItemActions } from '../components/dispatch/PartnerDocsWorkItemActions';

export interface WorkItem {
  id: string;
  case_id?: number;
  task_type?: string;
  description?: string | null;
  metadata?: {
    drafted_action?: {
      channel?: 'sms' | 'staff';
      body?: string;
      to?: string | null;
    };
    /** paperwork_missing items (T-186): what "Resend link" sends. */
    resend_link?: {
      quoteId: string;
      leadId?: string | null;
      email: string;
      clientName: string;
      clientId?: number;
    } | null;
  } | null;
}

/**
 * Review-and-act on an agent work item: edit + send the drafted SMS, snooze, or
 * dismiss. Each action hits the NestJS work-items endpoints and refreshes the list.
 */
export function WorkItemComposer({
  workItem,
  open,
  onOpenChange,
}: {
  workItem: WorkItem;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const notify = useNotify();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();
  const draft = workItem.metadata?.drafted_action;
  const isSms = draft?.channel === 'sms';
  const [body, setBody] = useState(draft?.body ?? '');
  const [busy, setBusy] = useState(false);
  const reviewer = identity
    ? { id: String(identity.id), name: identity.fullName }
    : undefined;

  const run = async (fn: () => Promise<unknown>, msg: string) => {
    setBusy(true);
    try {
      await fn();
      notify(msg, { type: 'success' });
      onOpenChange(false);
      refresh();
    } catch (e) {
      notify(`Failed: ${e instanceof Error ? e.message : String(e)}`, {
        type: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    // Stop clicks (incl. the portaled overlay/close, which bubble through the
    // React tree) from reaching the row's onClick → funeral navigation.
    <span onClick={(e) => e.stopPropagation()}>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="cleo-ui sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{workItem.description || 'Suggested action'}</DialogTitle>
        </DialogHeader>

        {isSms ? (
          <div className="flex flex-col gap-3">
            <div className="text-xs text-muted-foreground">SMS to {draft?.to}</div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              className="w-full rounded-md border border-border bg-card p-2 text-sm outline-none focus:ring-2 focus:ring-ring"
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => run(() => sendWorkItem(workItem.id, body, reviewer), 'Sent')}
                className="rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground disabled:opacity-60"
              >
                Send
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => run(() => snoozeWorkItem(workItem.id), 'Snoozed')}
                className="rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-60"
              >
                Snooze
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => run(() => dismissWorkItem(workItem.id, reviewer), 'Dismissed')}
                className="ml-auto rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:text-destructive disabled:opacity-60"
              >
                Dismiss
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="text-sm text-muted-foreground">
              {draft?.body || 'Staff action — open the case to handle it.'}
            </div>
            {workItem.task_type === 'paperwork_missing' && workItem.metadata?.resend_link && (
              <div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    const link = workItem.metadata!.resend_link!;
                    // The item stays open: it closes by itself once the family
                    // completes the form.
                    setBusy(true);
                    resendPaperworkLink(link)
                      .then(() => notify(`Paperwork link resent to ${link.email}`, { type: 'success' }))
                      .catch((e) => notify(`Failed: ${e instanceof Error ? e.message : String(e)}`, { type: 'error' }))
                      .finally(() => setBusy(false));
                  }}
                  className="rounded-md border border-border px-3 py-1.5 text-sm font-semibold disabled:opacity-60"
                >
                  Resend link
                </button>
              </div>
            )}
            {workItem.task_type === 'partner_documents' && workItem.case_id != null && (
              <PartnerDocsWorkItemActions
                caseId={workItem.case_id}
                workItemId={workItem.id}
                onResolved={() => {
                  onOpenChange(false);
                  refresh();
                }}
              />
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => run(() => confirmWorkItem(workItem.id), 'Confirmed')}
                className="rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground disabled:opacity-60"
              >
                Confirm
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => run(() => snoozeWorkItem(workItem.id), 'Snoozed')}
                className="rounded-md border border-border px-3 py-1.5 text-sm disabled:opacity-60"
              >
                Snooze
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => run(() => dismissWorkItem(workItem.id, reviewer), 'Dismissed')}
                className="ml-auto rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:text-destructive disabled:opacity-60"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
        </DialogContent>
      </Dialog>
    </span>
  );
}

export default WorkItemComposer;
